/**
 * The harness plumbing a recorded message arrives wrapped in.
 *
 * ⚠️ A TERMINAL SESSION RECORDS MORE THAN WHAT WAS TYPED. Running `/model` or
 * `!npm test` in a Claude Code terminal writes SEVERAL user records: a caveat
 * telling the agent to ignore what follows, the command's own name, its
 * message, its arguments, and then whatever the command printed — plus
 * `<system-reminder>` and `<task-notification>` blocks the harness injects on
 * its own. zevet was showing every one of them as prose, so a conversation
 * read back as XML and sessions were titled "Caveat: The messages below were
 * generated by the user while running local commands…".
 *
 * MEASURED in ~/.claude/projects and ~/.codex/sessions on 2026-09-22. Every
 * shape below is from a real file:
 *
 *   <command-name>/model</command-name>
 *   <command-message>model</command-message>
 *   <command-args></command-args>
 *   <command-message>max</command-message><command-name>/max</command-name>
 *   <command-name>workflow-authoring</command-name><skill-format>true</skill-format>
 *   <local-command-stdout>\x1b[2mCompacted (ctrl+o…)\x1b[22m</local-command-stdout>
 *   <bash-input>gcloud auth login</bash-input>
 *   <bash-stdout></bash-stdout><bash-stderr>bash: gcloud: not found</bash-stderr>
 *   <local-command-caveat>Caveat: The messages below…</local-command-caveat>
 *   <task-notification><task-id>…</task-id><status>completed</status>…
 *
 * WHAT THIS IS NOT. It is not the envelope PEEL in sessions.mjs, which takes
 * machine blocks off the FRONT of a prompt somebody then typed into. This
 * classifies a message that is NOTHING BUT envelope, which is the only case
 * where the whole thing can be replaced by a chip or dropped on the floor. A
 * message with words of its own outside the tags returns null and goes to the
 * peel, so "why does <div> collapse?" is still a question about markup.
 *
 * WHY .mjs: the gate runs `node --test` straight against the source tree and
 * cannot import TypeScript — the same reason transcript.mjs and sessions.mjs
 * are .mjs with a .d.mts beside them.
 */

/** @typedef {import("./envelope.d.mts").Envelope} Envelope */

/** Blocks that are the harness talking to the agent, never a person talking to
 *  anyone. codex's own two are here as well: its rollout files carry the same
 *  claude blocks, and `<environment_context>`/`<user_instructions>` besides. */
const NOISE = new Set([
  "local-command-caveat",
  "system-reminder",
  "task-notification",
  "environment_context",
  "user_instructions",
]);

/* Colour, cursor moves and OSC titles. `local-command-stdout` is whatever the
   command printed to a TTY, so `/compact` arrives dressed in \x1b[2m…\x1b[22m
   and a markdown block rendered it as literal escape bytes. */
const ANSI = /\u001b\[[0-9;?]*[ -/]*[@-~]|\u001b\][\s\S]*?(?:\u0007|\u001b\\)/g;

export function stripAnsi(text) {
  // A command that ran under a Windows shell prints CRLF, and a lone \r left
  // in a summary is a carriage return the line never comes back from.
  return String(text ?? "").replace(ANSI, "").replace(/\r\n?/g, "\n");
}

/** Top-level `<tag>…</tag>` blocks, or null the moment anything else appears —
 *  including an unclosed tag, which is what a truncated prompt looks like. */
function blocks(text) {
  let rest = String(text ?? "").trim();
  const out = [];
  while (rest) {
    const m = /^<([a-zA-Z][\w-]*)(?:\s[^>]*)?>([\s\S]*?)<\/\1>/.exec(rest);
    if (!m) return null;
    out.push([m[1], m[2]]);
    rest = rest.slice(m[0].length).trim();
  }
  return out.length ? out : null;
}

/**
 * What this message is, when it is only plumbing.
 *
 * @param {string} text
 * @returns {Envelope | null} null when a person wrote some of it.
 */
export function readEnvelope(text) {
  const found = blocks(text);
  if (!found) return null;
  const by = new Map(found);

  if (found.every(([tag]) => NOISE.has(tag))) return { kind: "noise" };

  if (by.has("command-name")) {
    /* `command-message` is the same name again and `command-args` is usually
       empty — the chip is the name plus whatever was typed after it, which is
       all Claude Code's own terminal shows. A skill is recorded without the
       slash it was invoked with; it gets it back. */
    const name = String(by.get("command-name")).trim().replace(/^\/+/, "");
    const args = String(by.get("command-args") ?? "").trim();
    return name ? { kind: "command", name: `/${name}`, args } : { kind: "noise" };
  }

  if (by.has("bash-input")) {
    const command = String(by.get("bash-input")).trim();
    return command ? { kind: "bash", command } : { kind: "noise" };
  }

  const out = ["bash-stdout", "local-command-stdout"]
    .concat(["bash-stderr", "local-command-stderr"])
    .filter((tag) => by.has(tag));
  if (out.length) {
    const body = stripAnsi(out.map((tag) => String(by.get(tag))).join("\n")).trim();
    const failed = out.some((tag) => tag.endsWith("stderr") && String(by.get(tag)).trim());
    // A command that printed nothing said nothing. `!mkdir x` records an empty
    // stdout and an empty stderr, and an empty grey box is worse than silence.
    return body ? { kind: "out", text: body, error: failed } : { kind: "noise" };
  }

  return null;
}
